import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

const NAV_LINKS = [
  { label: 'Home', href: '/' },
  { label: 'Services', href: '/services' },
  { label: 'About', href: '/about' },
  { label: 'Service Areas', href: '/#service-areas' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const handleAnchor = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    if (href.startsWith('/#')) {
      e.preventDefault();
      const id = href.replace('/#', '');
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: 'smooth' });
      setMobileOpen(false);
    } else {
      setMobileOpen(false);
    }
  };

  return (
    <header
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-500 ${
        scrolled ? 'bg-white border-b border-stone-200' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2 cursor-pointer">
          <img
            src="https://brainardsgreenscapes.com/assets/images/logo.png"
            alt="Brainard's Greenscapes"
            className="h-14 w-auto object-contain"
          />
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden lg:flex items-center gap-8">
          {NAV_LINKS.map((link) => (
            <a
              key={link.label}
              href={link.href}
              onClick={(e) => handleAnchor(e, link.href)}
              className={`font-sans text-sm font-medium tracking-wide transition-colors duration-300 cursor-pointer whitespace-nowrap ${
                scrolled
                  ? 'text-charcoal-800 hover:text-forest-500'
                  : 'text-stone-100 hover:text-white'
              }`}
            >
              {link.label}
            </a>
          ))}
        </nav>

        {/* CTA Buttons */}
        <div className="hidden lg:flex items-center gap-3">
          <a
            href="tel:5756358883"
            className={`font-sans text-sm font-medium flex items-center gap-2 transition-colors duration-300 cursor-pointer whitespace-nowrap ${
              scrolled ? 'text-forest-600 hover:text-forest-700' : 'text-stone-100 hover:text-white'
            }`}
          >
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-phone-line text-base"></i>
            </div>
            (575) 635-8883
          </a>
          <a
            href="#consultation"
            onClick={(e) => { e.preventDefault(); document.getElementById('consultation')?.scrollIntoView({ behavior: 'smooth' }); }}
            className="bg-forest-500 hover:bg-forest-600 text-white font-sans text-sm font-medium px-5 py-2.5 rounded-md transition-colors duration-300 cursor-pointer whitespace-nowrap"
          >
            Free Consultation
          </a>
        </div>

        {/* Mobile Toggle */}
        <button
          className={`lg:hidden w-10 h-10 flex items-center justify-center transition-colors ${scrolled ? 'text-charcoal-800' : 'text-white'} cursor-pointer`}
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-label="Toggle menu"
        >
          <i className={`ri-${mobileOpen ? 'close' : 'menu-3'}-line text-2xl`}></i>
        </button>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="lg:hidden bg-white border-t border-stone-200 px-6 py-4">
          {NAV_LINKS.map((link) => (
            <a
              key={link.label}
              href={link.href}
              onClick={(e) => handleAnchor(e, link.href)}
              className="block py-3 font-sans text-sm font-medium text-charcoal-800 hover:text-forest-500 border-b border-stone-100 last:border-0 cursor-pointer"
            >
              {link.label}
            </a>
          ))}
          <a
            href="tel:5756358883"
            className="mt-3 w-full flex items-center justify-center gap-2 bg-forest-500 text-white font-sans text-sm font-medium py-3 rounded-md cursor-pointer"
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-phone-line"></i>
            </div>
            Call (575) 635-8883
          </a>
        </div>
      )}
    </header>
  );
}